import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import cogoToast from 'cogo-toast';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTrash, faTimes } from '@fortawesome/pro-regular-svg-icons';

import Modal from '../General/Modal';
import {
  getWaitlistBlockedCountries,
  createWaitlistBlockedCountry,
  updateWaitlistBlockedCountry,
  deleteWaitlistBlockedCountry,
  getAvailableCountries
} from '../../api/waitlist_blocked_countries';

import './WaitlistBlockedCountriesModal.scss';

const TIER_OPTIONS = [
  { value: 'hard_block', label: 'Hard block' },
  { value: 'raised_threshold', label: 'Raised threshold (5K followers)' }
];

const tierLabel = tier => TIER_OPTIONS.find(t => t.value === tier)?.label || tier;

const WaitlistBlockedCountriesModal = ({ close }) => {
  const [blocks, setBlocks] = useState([]);
  const [allCountries, setAllCountries] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [newCountryCode, setNewCountryCode] = useState('');
  const [newTier, setNewTier] = useState('hard_block');
  const [newNotes, setNewNotes] = useState('');

  useEffect(() => {
    Promise.all([getWaitlistBlockedCountries(), getAvailableCountries()])
      .then(([blocksRes, countriesRes]) => {
        setBlocks(blocksRes.blocks || []);
        setAllCountries(countriesRes.countries || []);
      })
      .catch(() => cogoToast.error('Error loading country blocks'))
      .finally(() => setIsLoading(false));
  }, []);

  const blockedCodes = new Set(blocks.map(b => b.countryCode));
  const availableCountries = allCountries.filter(c => !blockedCodes.has(c.code));
  const countryName = code => allCountries.find(c => c.code === code)?.name || '';

  const addBlock = () => {
    if (isSaving) return;
    if (!newCountryCode) return cogoToast.error('Pick a country to block.');
    setIsSaving(true);
    createWaitlistBlockedCountry({ countryCode: newCountryCode, tier: newTier, notes: newNotes || null })
      .then(created => {
        // apiBase add returns the created row at the top level (id present).
        const newBlock = created?.id ? created : created?.WaitlistBlockedCountry || created;
        setBlocks(prev => [...prev, newBlock].sort((a, b) => a.countryCode.localeCompare(b.countryCode)));
        setNewCountryCode('');
        setNewTier('hard_block');
        setNewNotes('');
        cogoToast.success('Country added — will apply to new signups on the next hourly batch.');
      })
      .catch(err => cogoToast.error(err || 'Error adding country.'))
      .finally(() => setIsSaving(false));
  };

  const updateTier = (block, tier) => {
    if (isSaving || tier === block.tier) return;
    const prevBlocks = blocks;
    setBlocks(prev => prev.map(b => (b.id === block.id ? { ...b, tier } : b)));
    setIsSaving(true);
    updateWaitlistBlockedCountry(block.id, { tier })
      .catch(() => {
        setBlocks(prevBlocks);
        cogoToast.error('Error updating tier.');
      })
      .finally(() => setIsSaving(false));
  };

  const removeBlock = block => {
    if (isSaving) return;
    if (!window.confirm(`Remove ${block.countryCode} from the block list?`)) return;
    const prevBlocks = blocks;
    setBlocks(prev => prev.filter(b => b.id !== block.id));
    setIsSaving(true);
    deleteWaitlistBlockedCountry(block.id)
      .then(() => cogoToast.success(`${block.countryCode} removed.`))
      .catch(() => {
        setBlocks(prevBlocks);
        cogoToast.error('Error removing country.');
      })
      .finally(() => setIsSaving(false));
  };

  return (
    <Modal visible={true} close={close}>
      <div className='waitlist-country-blocks-modal-outer'>
        <div className='close' onClick={close}>
          <FontAwesomeIcon icon={faTimes} />
        </div>
        <div className='waitlist-country-blocks-modal-title'>Blocked Countries</div>
        <div className='waitlist-country-blocks-modal-subtitle'>
          <p>
            <strong>Hard block</strong> auto-rejects every applicant from the country, regardless of follower count or connected accounts.
            <strong> Raised threshold</strong> requires 5,000 followers instead of the default 1,000, plus an active Instagram connection
            matching the application or manual review.
          </p>
          {/* Keep these regions aligned with applicantIsInBlacklistedCountry in waitlist_decision_helpers. */}
          <p>
            The hard-block rule also applies to these Ukrainian IP regions: Crimea (UA-43), Sevastopol (UA-40), Donetsk (UA-14), Luhansk
            (UA-09), Kherson (UA-65), and Zaporizhzhia (UA-23). These fixed regional rules apply in addition to the editable countries below.
          </p>
          <p>
            Auto-decline runs as an hourly batch and only processes signups older than 1 hour, so a newly-added country typically starts blocking
            within 1–2 hours of being added. Already-accepted or already-rejected applicants are not affected — rules only apply to pending signups.
          </p>
        </div>

        <div className='waitlist-country-blocks-list'>
          {isLoading && <div className='waitlist-country-blocks-empty'>Loading…</div>}
          {!isLoading && blocks.length === 0 && <div className='waitlist-country-blocks-empty'>No countries blocked.</div>}
          {!isLoading &&
            blocks.map(block => (
              <div key={block.id} className='waitlist-country-blocks-row'>
                <div className='waitlist-country-blocks-row-code'>
                  <strong>{block.countryCode}</strong>
                  <span>{countryName(block.countryCode)}</span>
                </div>
                <select value={block.tier} onChange={e => updateTier(block, e.target.value)} disabled={isSaving}>
                  {TIER_OPTIONS.map(t => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
                <div className='waitlist-country-blocks-row-notes'>{block.notes || ''}</div>
                <FontAwesomeIcon icon={faTrash} onClick={() => removeBlock(block)} />
              </div>
            ))}
        </div>

        <div className='waitlist-country-blocks-add'>
          <select value={newCountryCode} onChange={e => setNewCountryCode(e.target.value)} disabled={isSaving}>
            <option value=''>Add country…</option>
            {availableCountries.map(c => (
              <option key={c.code} value={c.code}>{c.code} — {c.name}</option>
            ))}
          </select>
          <select value={newTier} onChange={e => setNewTier(e.target.value)} disabled={isSaving}>
            {TIER_OPTIONS.map(t => (
              <option key={t.value} value={t.value}>{tierLabel(t.value)}</option>
            ))}
          </select>
          <input
            type='text'
            placeholder='Notes (optional)'
            value={newNotes}
            onChange={e => setNewNotes(e.target.value)}
            disabled={isSaving}
          />
          <button onClick={addBlock} disabled={isSaving || !newCountryCode}>
            <FontAwesomeIcon icon={faPlus} /> Add
          </button>
        </div>
      </div>
    </Modal>
  );
};

WaitlistBlockedCountriesModal.propTypes = {
  close: PropTypes.func.isRequired
};

export default WaitlistBlockedCountriesModal;
